/** `subscription_pause_events.action` (CHECK constraint, CWS-1543 / CWS-1624). */
export type PauseEventAction =
  | 'paused'
  | 'scheduled'
  | 'scheduled_canceled'
  | 'early_resume'
  | 'auto_resume'
  | 'billing_restarted'
  | 'membership_canceled';

/** `subscription_pause_notifications.status` (CWS-1618). */
export type PauseSmsStatus = 'pending' | 'attempting' | 'sent' | 'failed' | 'skipped';

/** SMS filter: a delivery status, or an SMS-eligible event that never got a notification row. */
export type PauseSmsFilter = PauseSmsStatus | 'not_queued';

/** Pill colour; maps to a `pause-events-pill--*` modifier. */
export type PauseEventVariant = 'neutral' | 'info' | 'positive' | 'warning' | 'danger';

/** Dates and banked days only (no personal data). Keys present depend on the action. */
export interface PauseEventPayload {
  start?: string | null;
  resume_date?: string | null;
  banked_days?: number | null;
  next_charge_date?: string | null;
  billing_restart_at?: string | null;
  original_resume_date?: string | null;
  pause_status?: string | null;
  source?: string | null;
  reason?: string | null;
  [key: string]: unknown;
}

/** Raw row from `GET /admin/super-admin/companies/:id/membership-pause-events`. */
export interface MembershipPauseEvent {
  id: string;
  subscription_id: string;
  action: PauseEventAction;
  payload: PauseEventPayload;
  created_at: string;
  pause_status: string | null;
  package_name: string | null;
  customer_name: string | null;
  license_plate: string | null;
  /** IANA zone of the membership's location; every date on the row is shown in it. */
  time_zone: string;
  performed_by: string | null;
  performed_by_name: string | null;
  performed_by_email: string | null;
  sms_eligible: boolean;
  sms_status: PauseSmsStatus | null;
  sms_attempt_count: number | null;
  sms_sent_at: string | null;
  sms_failure_reason: string | null;
  sms_provider_message_id: string | null;
}

export interface PauseSmsState {
  kind: PauseSmsStatus | 'not_queued' | 'na';
  label: string;
  variant: PauseEventVariant | null;
  detail: string;
}

/** What the template renders: every label and string is pre-computed by the rules layer. */
export interface MembershipPauseEventRow {
  id: string;
  subscriptionId: string;
  timestamp: string;
  /** IANA zone the row's dates are shown in; the timestamp carries its abbreviation. */
  timeZone: string;
  customerName: string;
  licensePlate: string;
  planName: string;
  actionLabel: string;
  actionVariant: PauseEventVariant;
  /** One labelled line per fact (Start, Resume, Banked, Next charge). */
  pauseWindowLines: string[];
  performedBy: string;
  sms: PauseSmsState;
  providerMessageId: string;
  payload: PauseEventPayload;
}

export interface MembershipPauseEventListParams {
  page?: number;
  limit?: number;
  action?: PauseEventAction;
  sms_status?: PauseSmsFilter;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export interface MembershipPauseEventListResult {
  events: MembershipPauseEvent[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
}

/** Filters applied from the Filter modal. A date range only counts once both ends are set. */
export interface PauseEventAppliedFilters {
  action: PauseEventAction | '';
  smsStatus: PauseSmsFilter | '';
  dateFrom: Date | null;
  dateTo: Date | null;
}

export const DEFAULT_PAUSE_EVENT_FILTERS: PauseEventAppliedFilters = {
  action: '',
  smsStatus: '',
  dateFrom: null,
  dateTo: null,
};
